import { useState, useEffect } from "react";
import { GripVertical, Pencil, Trash2, Plus, Palette, Smile } from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { addToast } from "@web/features/toast/toast-service";
import { getErrorMessage } from "@repo/share-log-web-utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@web/components/ui/dialog";
import { Button } from "@web/components/ui/button";
import { Input } from "@web/components/ui/input";
import { Badge } from "@web/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@web/components/ui/popover";
import { ColorPicker } from "@web/components/ui/color-picker";
import { ColorBadge } from "@web/components/ui/color-badge";
import { IconPicker } from "@web/components/ui/icon-picker";
import { MethodIcon } from "@web/components/ui/method-icon";
import { useMethodStore } from "@web/stores";
import type { LogMethod } from "@repo/share-log-interfaces";
import { modalService } from "@web/features/modal";

interface MethodManageDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: number;
  isMaster: boolean;
}

function SortableMethodItem({
  method,
  isMaster,
  onEdit,
  onDelete,
  isSubmitting,
}: {
  method: LogMethod;
  isMaster: boolean;
  onEdit: (method: LogMethod) => void;
  onDelete: (id: number) => void;
  isSubmitting: boolean;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: method.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const isDefault = !!method.defaultType;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center justify-between p-2 border rounded"
    >
      <div className="flex items-center gap-2 flex-1 min-w-0">
        {isMaster && (
          <button
            type="button"
            className="cursor-grab active:cursor-grabbing p-1"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="h-4 w-4 text-muted-foreground" />
          </button>
        )}
        <ColorBadge
          name={method.name}
          color={method.color}
          icon={method.icon}
        />
        {isDefault && <Badge variant="muted">기본</Badge>}
      </div>
      <div className="flex gap-1 shrink-0">
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={() => onEdit(method)}
        >
          <Pencil className="h-4 w-4" />
        </Button>
        {isMaster && !isDefault && (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-destructive"
            onClick={() => onDelete(method.id)}
            disabled={isSubmitting}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}

export function MethodManageDialog({
  open,
  onOpenChange,
  workspaceId,
  isMaster,
}: MethodManageDialogProps) {
  const { methods, fetchMethods, createMethod, updateMethod, deleteMethod } =
    useMethodStore();

  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState<string | null>(null);
  const [newIcon, setNewIcon] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState("");
  const [editingColor, setEditingColor] = useState<string | null>(null);
  const [editingIcon, setEditingIcon] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  useEffect(() => {
    if (open) {
      void fetchMethods(workspaceId);
    }
  }, [open, workspaceId, fetchMethods]);

  const handleCreate = async () => {
    if (isSubmitting || !newName.trim()) return;
    setIsSubmitting(true);
    try {
      await createMethod(workspaceId, {
        name: newName.trim(),
        ...(newColor && { color: newColor }),
        ...(newIcon && { icon: newIcon }),
      });
      setNewName("");
      setNewColor(null);
      setNewIcon(null);
    } catch (error) {
      addToast({ type: "error", message: getErrorMessage(error) });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStartEdit = (method: LogMethod) => {
    setEditingId(method.id);
    setEditingName(method.name);
    setEditingColor(method.color ?? null);
    setEditingIcon(method.icon ?? null);
  };

  const handleSaveEdit = async () => {
    if (!editingId || !editingName.trim()) return;
    setIsSubmitting(true);
    try {
      const editingMethod = methods.find((m) => m.id === editingId);
      const colorChanged = editingColor !== (editingMethod?.color ?? null);
      const iconChanged = editingIcon !== (editingMethod?.icon ?? null);
      await updateMethod(workspaceId, editingId, {
        name: editingName.trim(),
        ...(colorChanged && { color: editingColor }),
        ...(iconChanged && { icon: editingIcon }),
      });
      setEditingId(null);
      setEditingName("");
      setEditingColor(null);
      setEditingIcon(null);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingName("");
    setEditingColor(null);
    setEditingIcon(null);
  };

  const handleDelete = async (methodId: number) => {
    const confirmed = await modalService.destructive("정말 삭제하시겠습니까?");
    if (!confirmed) return;
    setIsSubmitting(true);
    try {
      await deleteMethod(workspaceId, methodId);
    } catch (error) {
      addToast({ type: "error", message: getErrorMessage(error) });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = methods.findIndex((m) => m.id === active.id);
    const newIndex = methods.findIndex((m) => m.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(methods, oldIndex, newIndex);

    try {
      await Promise.all(
        reordered.map((m, idx) =>
          updateMethod(workspaceId, m.id, { sortOrder: idx }),
        ),
      );
      void fetchMethods(workspaceId);
    } catch (error) {
      addToast({ type: "error", message: getErrorMessage(error) });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>결제 수단 관리</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="flex gap-2">
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="새 수단 이름"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleCreate();
                }
              }}
            />
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="icon" className="shrink-0">
                  {newColor ? (
                    <div
                      className="w-4 h-4 rounded-sm"
                      style={{ backgroundColor: newColor }}
                    />
                  ) : (
                    <Palette className="h-4 w-4" />
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto">
                <ColorPicker value={newColor} onChange={setNewColor} />
              </PopoverContent>
            </Popover>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="icon" className="shrink-0">
                  {newIcon ? (
                    <MethodIcon name={newIcon} />
                  ) : (
                    <Smile className="h-4 w-4" />
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto">
                <IconPicker value={newIcon} onChange={setNewIcon} />
              </PopoverContent>
            </Popover>
            <Button
              onClick={handleCreate}
              disabled={isSubmitting || !newName.trim()}
            >
              <Plus className="h-4 w-4 mr-1" />
              추가
            </Button>
          </div>

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={methods.map((m) => m.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {methods.map((method) =>
                  editingId === method.id ? (
                    <div
                      key={method.id}
                      className="flex items-center justify-between p-2 border rounded"
                    >
                      <div className="flex gap-2 flex-1 items-center">
                        <Input
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleSaveEdit();
                            if (e.key === "Escape") handleCancelEdit();
                          }}
                          autoFocus
                        />
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              size="icon"
                              className="shrink-0 h-8 w-8"
                            >
                              {editingColor ? (
                                <div
                                  className="w-4 h-4 rounded-sm"
                                  style={{ backgroundColor: editingColor }}
                                />
                              ) : (
                                <Palette className="h-4 w-4" />
                              )}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto">
                            <ColorPicker
                              value={editingColor}
                              onChange={setEditingColor}
                            />
                          </PopoverContent>
                        </Popover>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              size="icon"
                              className="shrink-0 h-8 w-8"
                            >
                              {editingIcon ? (
                                <MethodIcon name={editingIcon} />
                              ) : (
                                <Smile className="h-4 w-4" />
                              )}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto">
                            <IconPicker
                              value={editingIcon}
                              onChange={setEditingIcon}
                            />
                          </PopoverContent>
                        </Popover>
                        <Button
                          size="sm"
                          onClick={handleSaveEdit}
                          disabled={isSubmitting}
                        >
                          저장
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleCancelEdit}
                          disabled={isSubmitting}
                        >
                          취소
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <SortableMethodItem
                      key={method.id}
                      method={method}
                      isMaster={isMaster}
                      onEdit={handleStartEdit}
                      onDelete={handleDelete}
                      isSubmitting={isSubmitting}
                    />
                  ),
                )}
                {methods.length === 0 && (
                  <p className="text-center text-muted-foreground py-4">
                    등록된 결제 수단이 없습니다.
                  </p>
                )}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      </DialogContent>
    </Dialog>
  );
}
